import { Controller } from "@hotwired/stimulus"

// UI-08 form_validate controller (Plan 04-11).
//
// Generic client-side pattern check — UX sugar only. The server-side regex
// in CattleLookupsController#create is authoritative (`\A\d{12}\z`); this
// controller shows an inline error label while the user is typing but does
// NOT disable the submit button — disabling would break the server-side
// validation gate's reachability from system tests (and from users with
// hostile / mistyped input who try to submit anyway).
//
// Auto-registered via `controllers/index.js` esbuild glob (Plan 04-02 LOCK —
// do NOT edit index.js).
//
// Values:
//   pattern — RegExp source string. Defaults to `^\d{12}$` for backward
//             compatibility if the caller forgets the data-attribute.
//   loadingLabel — text shown on the submit button while the form is being
//                  submitted (UI-SPEC §UI-08 "조회 중입니다...").
//
// Targets:
//   input  — the text field whose value drives validation
//   error  — the inline error message (toggles `hidden` class)
//   submit — the submit button. Kept enabled until the user clicks submit;
//            on submit we swap to `loading loading-dots` + loadingLabel and
//            disable to prevent multi-click during the ekape external call
//            (UI-SPEC §UI-08 Interaction "Loading state").
export default class extends Controller {
  static values = {
    pattern: { type: String, default: "^\\d{12}$" },
    loadingLabel: { type: String, default: "조회 중입니다..." }
  }
  static targets = ["input", "submit", "error"]

  connect() {
    this.validate()
    // Form submit hook — server-side regex is still authoritative; we only
    // upgrade the button visuals once the browser has accepted the submit.
    this.boundOnSubmit = this.onSubmit.bind(this)
    this.element.addEventListener("submit", this.boundOnSubmit)
  }

  disconnect() {
    if (this.boundOnSubmit) {
      this.element.removeEventListener("submit", this.boundOnSubmit)
    }
  }

  validate() {
    const re = new RegExp(this.patternValue)
    const value = (this.hasInputTarget ? this.inputTarget.value : "").trim()
    const empty = value.length === 0
    const ok = re.test(value)

    if (this.hasErrorTarget) {
      // Hide while empty (don't yell at the user before they type) or valid.
      this.errorTarget.classList.toggle("hidden", empty || ok)
    }
  }

  onSubmit() {
    if (!this.hasSubmitTarget) return
    // Only swap to loading once client-side validation passed; the form
    // would otherwise have been blocked by the browser's pattern check.
    const re = new RegExp(this.patternValue)
    const value = (this.hasInputTarget ? this.inputTarget.value : "").trim()
    if (!re.test(value)) return

    const btn = this.submitTarget
    // <input type="submit"> uses `value`; <button> uses textContent — handle both.
    if (btn.tagName === "INPUT") {
      btn.value = this.loadingLabelValue
    } else {
      btn.textContent = this.loadingLabelValue
    }
    btn.classList.add("loading", "loading-dots")
    btn.disabled = true
  }
}
